import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  api,
  formatApiError,
  setActingClient,
  getActingClient,
} from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // ==================================================
  // USER STATE
  // ==================================================

  // null = loading
  // false = guest
  // object = authenticated user

  const [user, setUser] = useState(null);

  const [bootstrapped, setBootstrapped] = useState(false);

  // ==================================================
  // ADVISOR "ACT AS CLIENT" CONTEXT
  // ==================================================
  // The selected client an advisor is currently viewing. Persisted so a page
  // refresh keeps the same client context. { id, name } or null.
  const [activeClient, setActiveClientState] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("pfa_active_client_meta");
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });

  const selectClient = useCallback((client) => {
    // client = { id, name } | null
    setActingClient(client ? client.id : null);
    setActiveClientState(client || null);
    if (typeof window !== "undefined") {
      if (client)
        window.localStorage.setItem(
          "pfa_active_client_meta",
          JSON.stringify(client),
        );
      else window.localStorage.removeItem("pfa_active_client_meta");
    }
  }, []);

  const clearClient = useCallback(() => selectClient(null), [selectClient]);

  // ==================================================
  // LOAD CURRENT USER
  // ==================================================

  const refresh = useCallback(async () => {
    // Prevent repeated guest refresh spam

    if (user === false) {
      return null;
    }

    try {
      const { data } = await api.get("/auth/me");

      setUser(data);

      return data;
    } catch (e) {
      setUser(false);

      return null;
    } finally {
      setBootstrapped(true);
    }
  }, [user]);

  // ==================================================
  // INITIAL APP LOAD
  // ==================================================

  useEffect(() => {
    if (!bootstrapped) {
      refresh();
    }
  }, [bootstrapped, refresh]);

  // ==================================================
  // LOGIN
  // ==================================================

  const login = async (payload) => {
    const { data } = await api.post("/auth/login", payload);

    setUser(data.user);

    return data.user;
  };

  // ==================================================
  // REGISTER
  // ==================================================

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);

    setUser(data.user);

    return data.user;
  };

  // ==================================================
  // SOCIAL LOGIN (Google / Apple)
  // ==================================================

  const oauthLogin = async (provider, payload) => {
    const { data } = await api.post(`/auth/oauth/${provider}`, payload);

    setUser(data.user);

    return data.user;
  };

  // ==================================================
  // START INTERACTIVE DEMO
  // ==================================================

  const startDemo = async (persona) => {
    const { data } = await api.post(`/demo/${persona}/start`);

    setUser(data.user);

    return data.user;
  };

  // ==================================================
  // LOGOUT
  // ==================================================

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}

    clearClient();
    setUser(false);
  };

  // ==================================================
  // CONTEXT VALUE
  // ==================================================

  const value = {
    user,
    bootstrapped,
    login,
    register,
    oauthLogin,
    startDemo,
    logout,
    refresh,
    formatApiError,
    // Advisor client-context
    activeClient,
    selectClient,
    clearClient,
    isAdvisor: !!user && user.role === "pfa",
    // True when an advisor is viewing a client who granted only "view" access —
    // every create/edit/delete affordance must be hidden in that mode.
    viewOnly: !!activeClient && activeClient.permission === "view",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ======================================================
// HOOK
// ======================================================

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return ctx;
}
